import { Mail } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { PageFooter } from "@/components/PageFooter";
import { globalCoordinators, countryOwners } from "@/data/contacts";
import { useLang } from "@/i18n";

const onboardStatus = {
  HR: { step: 8, status: "Live", color: "bg-emerald-50 text-emerald-700" },
  BA: { step: 8, status: "Live", color: "bg-emerald-50 text-emerald-700" },
  SI: { step: 8, status: "Live", color: "bg-emerald-50 text-emerald-700" },
  PL: { step: 8, status: "Live", color: "bg-emerald-50 text-emerald-700" },
  RO: { step: 8, status: "Live", color: "bg-emerald-50 text-emerald-700" },
  MK: { step: 8, status: "Live", color: "bg-emerald-50 text-emerald-700" },
  RS: { step: 8, status: "Live", color: "bg-emerald-50 text-emerald-700" },
  ME: { step: 8, status: "Live", color: "bg-emerald-50 text-emerald-700" },
  XK: { step: 8, status: "Live", color: "bg-emerald-50 text-emerald-700" },
  AT: { step: 2, status: "Step 2–3", color: "bg-blue-50 text-blue-700" },
  AM: { step: 2, status: "Step 2–3", color: "bg-blue-50 text-blue-700" },
  MD: { step: 2, status: "Step 2–3", color: "bg-blue-50 text-blue-700" },
};

export const Contacts = ({ onNavigate }) => {
  const { t } = useLang();
  return (
    <div data-testid="contacts-view">
      {/* Title */}
      <div className="mb-12">
        <span className="cchbc-eyebrow mb-5">{t("Directory")}</span>
        <h1 className="font-condensed text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-[#000000] mb-4 mt-4 leading-[0.95]" data-testid="contacts-title">
          {t("Contacts")}
        </h1>
        <p className="text-[#555555] text-base leading-relaxed max-w-2xl">
          {t("Global coordinators and Coca-Cola HBC country owners across all markets")}
        </p>
      </div>

      {/* Global Coordinators */}
      <div className="mb-14" data-testid="global-coordinators">
        <div className="mb-6 pb-4 border-b border-[#e8e8e8]">
          <span className="cchbc-eyebrow mb-3">{t("Strategy & Cross-Market")}</span>
          <h2 className="font-condensed text-2xl sm:text-3xl font-black uppercase tracking-tight text-[#000000] mt-3">{t("Global Coordinators")}</h2>
          <p className="text-sm text-[#555555] mt-2 max-w-2xl">
            {t("Coordinating FORA programme across all Coca-Cola HBC markets. Contact for cross-market topics, strategy, and escalations.")}
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {globalCoordinators.map((c) => (
            <div key={c.email} className="bg-white border border-[#e8e8e8] rounded-sm p-6 hover:border-[#000000] transition-colors group">
              <div className="w-12 h-12 rounded-sm bg-[#000000] flex items-center justify-center mb-4">
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-white">{c.name.split(" ").map(n => n[0]).join("")}</span>
              </div>
              <p className="font-condensed text-lg font-bold uppercase tracking-tight text-[#000000] mb-1">{c.name}</p>
              <p className="text-[10px] text-[#555555] mb-4 uppercase tracking-[0.1em] font-semibold">{t(c.role)}</p>
              <a href={`mailto:${c.email}`} className="inline-flex items-center gap-2 text-xs text-[#dd260e] font-semibold hover:underline break-all">
                <Mail className="w-3 h-3 shrink-0" />{c.email}
              </a>
            </div>
          ))}
        </div>
      </div>

      <Separator className="bg-[#e8e8e8] mb-14" />

      {/* Country Owners Table */}
      <div className="mb-10" data-testid="country-owners-section">
        <div className="mb-6 pb-4 border-b border-[#e8e8e8]">
          <span className="cchbc-eyebrow mb-3">{t("By Market")}</span>
          <h2 className="font-condensed text-2xl sm:text-3xl font-black uppercase tracking-tight text-[#000000] mt-3">{t("CC HBC Country Owners")}</h2>
          <p className="text-sm text-[#555555] mt-2 max-w-2xl">
            {t("Each market has a dedicated Coca-Cola HBC country owner responsible for the FORA programme locally.")}
          </p>
        </div>

        <div className="bg-white border border-[#e8e8e8] rounded-sm overflow-hidden">
          {/* Desktop table */}
          <div className="hidden sm:block">
            <div className="grid grid-cols-[1fr_1fr_1fr_120px] bg-[#000000] text-white">
              <div className="px-5 py-3 text-[10px] font-semibold uppercase tracking-wider">{t("Country")}</div>
              <div className="px-5 py-3 text-[10px] font-semibold uppercase tracking-wider">{t("Owner")}</div>
              <div className="px-5 py-3 text-[10px] font-semibold uppercase tracking-wider">{t("Email")}</div>
              <div className="px-5 py-3 text-[10px] font-semibold uppercase tracking-wider">{t("Status")}</div>
            </div>
            {countryOwners.map((c, i) => {
              const status = onboardStatus[c.country];
              return (
                <div key={c.country} className={`grid grid-cols-[1fr_1fr_1fr_120px] ${i < countryOwners.length - 1 ? "border-b border-[#e8e8e8]" : ""} hover:bg-[#fafafa] transition-colors`}>
                  <div className="px-5 py-3.5 flex items-center gap-2">
                    <span className="w-7 h-7 bg-[#000000] text-white text-[10px] font-bold rounded-sm flex items-center justify-center shrink-0">{c.country}</span>
                    <span className="text-sm font-medium text-[#000000]">{t(c.name)}</span>
                  </div>
                  <div className="px-5 py-3.5 text-sm text-[#555555]">{c.owner}</div>
                  <div className="px-5 py-3.5">
                    {c.email ? (
                      <a href={`mailto:${c.email}`} className="text-xs text-[#dd260e] hover:underline break-all">{c.email}</a>
                    ) : (
                      <span className="text-xs text-[#555555]">—</span>
                    )}
                  </div>
                  <div className="px-5 py-3.5">
                    {status && (
                      <Badge className={`${status.color} border-transparent rounded-sm text-[10px] font-semibold hover:opacity-100`}>
                        {t(status.status)}
                      </Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Mobile cards */}
          <div className="sm:hidden divide-y divide-[#e8e8e8]">
            {countryOwners.map((c) => {
              const status = onboardStatus[c.country];
              return (
                <div key={c.country} className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="w-7 h-7 bg-[#000000] text-white text-[10px] font-bold rounded-sm flex items-center justify-center">{c.country}</span>
                      <span className="text-sm font-bold text-[#000000]">{t(c.name)}</span>
                    </div>
                    {status && (
                      <Badge className={`${status.color} border-transparent rounded-sm text-[10px] font-semibold hover:opacity-100`}>
                        {t(status.status)}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-[#555555] mb-1">{c.owner}</p>
                  {c.email ? (
                    <a href={`mailto:${c.email}`} className="text-xs text-[#dd260e] hover:underline">{c.email}</a>
                  ) : (
                    <span className="text-xs text-[#555555]">—</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center gap-1.5">
          <Badge className="bg-emerald-50 text-emerald-700 border-transparent rounded-sm text-[10px] font-semibold hover:opacity-100">{t("Live")}</Badge>
          <span className="text-xs text-[#555555]">{t("Fully onboarded (Step 8/8)")}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Badge className="bg-blue-50 text-blue-700 border-transparent rounded-sm text-[10px] font-semibold hover:opacity-100">{t("Step 2–3")}</Badge>
          <span className="text-xs text-[#555555]">{t("Defining scope & budget")}</span>
        </div>
      </div>

      <PageFooter />
    </div>
  );
};
